import type {ActionsStatus} from '../modules/helmfold-actions.ts';
import type {LogLine, LogLineCommand} from './ActionRunView.ts';
import {parseLogLineCommand} from './ActionRunView.ts';

export type Step = {
  summary: string;
  duration: string;
  status: ActionsStatus;
};

export type LogItem = {key: string; kind: 'step-header'; step: number} |
  {key: string; kind: 'line'; step: number; line: LogLine; cmd: LogLineCommand | null};

export type GroupSpan = {
  step: number;
  startFlatIndex: number;
  endFlatIndex: number | null; // null while the group is still open (unterminated or still streaming)
};

export type LogModel = {
  getFlat(): LogItem[];
  getVisible(): LogItem[];
  getFlatIndexForKey(key: string): number;
  getGroupSpan(key: string): GroupSpan | undefined;
  ensureStepHeader(step: number): void;
  appendStepLines(step: number, lines: LogLine[]): void;
  isStepExpanded(step: number): boolean;
  setStepExpanded(step: number, expanded: boolean): void;
  isGroupExpanded(groupKey: string): boolean;
  setGroupExpanded(groupKey: string, expanded: boolean): void;
};

export function createLogModel(): LogModel {
  const flat: LogItem[] = [];
  const flatIndexByKey = new Map<string, number>();
  const stepHeaderSeen = new Set<number>();
  const headerFlatIndex = new Map<number, number>(); // step -> flat index of its step-header item
  const stepInsertIndex = new Map<number, number>(); // step -> flat index where its NEXT appended line goes
  const stepExpandedState = new Map<number, boolean>();
  const stepOpenGroupKey = new Map<number, string | null>();
  const groups = new Map<string, GroupSpan>();
  const groupExpandedState = new Map<string, boolean>();
  let visible: LogItem[] = [];
  let visibleDirty = true;

  // Splices `items` into `flat` at `index`, then shifts every other piece of position-based
  // bookkeeping (step insertion points, header positions, group spans) that sits at or after
  // `index`, and rebuilds flatIndexByKey for the shifted tail. This is what makes it safe to
  // append content for a step that isn't the most-recently-appended one -- see the "a step
  // expanding after a later step..." test below, covering round-2 review finding S-3 (a step
  // can be expanded, and stream its first lines, after a later-index step already has content,
  // because the backend skips a step's log data entirely while its cursor isn't Expanded).
  function insertAt(index: number, items: LogItem[]) {
    if (items.length === 0) return;
    flat.splice(index, 0, ...items);
    const shift = items.length;
    for (const [step, pos] of stepInsertIndex) if (pos > index) stepInsertIndex.set(step, pos + shift);
    for (const [step, pos] of headerFlatIndex) if (pos >= index) headerFlatIndex.set(step, pos + shift);
    for (const span of groups.values()) {
      if (span.startFlatIndex >= index) span.startFlatIndex += shift;
      if (span.endFlatIndex !== null && span.endFlatIndex >= index) span.endFlatIndex += shift;
    }
    for (let i = index; i < flat.length; i++) flatIndexByKey.set(flat[i].key, i);
  }

  function ensureStepHeader(step: number) {
    if (stepHeaderSeen.has(step)) return;
    stepHeaderSeen.add(step);
    // insert in step-index order: right before the header of the smallest already-known step
    // with a higher index, or at the end if this is the highest step index seen so far.
    let insertIndex = flat.length;
    for (const [otherStep, idx] of headerFlatIndex) {
      if (otherStep > step) insertIndex = Math.min(insertIndex, idx);
    }
    insertAt(insertIndex, [{key: `step:${step}`, kind: 'step-header', step}]);
    headerFlatIndex.set(step, insertIndex);
    stepInsertIndex.set(step, insertIndex + 1);
    if (!stepExpandedState.has(step)) stepExpandedState.set(step, false);
    visibleDirty = true;
  }

  function appendStepLines(step: number, lines: LogLine[]) {
    if (!stepHeaderSeen.has(step)) ensureStepHeader(step);
    const insertIndex = stepInsertIndex.get(step)!;
    const newItems: LogItem[] = [];
    const opens: Array<{key: string; localOffset: number}> = [];
    const closes: Array<{key: string; localOffset: number}> = [];
    let currentOpenKey: string | null = stepOpenGroupKey.get(step) ?? null;

    for (const line of lines) {
      const cmd = parseLogLineCommand(line);
      if (cmd?.name === 'hidden') continue;
      if (cmd?.name === 'endgroup') {
        if (currentOpenKey) {
          closes.push({key: currentOpenKey, localOffset: newItems.length});
          currentOpenKey = null;
        }
        continue;
      }
      const key = `${step}:${line.index}`;
      if (cmd?.name === 'group') {
        if (currentOpenKey) closes.push({key: currentOpenKey, localOffset: newItems.length}); // implicit close
        newItems.push({key, kind: 'line', step, line, cmd});
        opens.push({key, localOffset: newItems.length - 1});
        currentOpenKey = key;
        continue;
      }
      newItems.push({key, kind: 'line', step, line, cmd});
    }

    insertAt(insertIndex, newItems); // shifts any pre-existing state at/after insertIndex first

    // Update insertion point for this step (insertAt shifts only later steps' insertion points via > comparison)
    stepInsertIndex.set(step, insertIndex + newItems.length);

    // Only after insertAt has run do we know the final flat positions of THIS batch's items --
    // insertIndex itself is stable (insertAt spliced exactly there), so insertIndex + localOffset
    // is correct. Do this after insertAt, not before, or these spans would get double-shifted.
    for (const o of opens) groups.set(o.key, {step, startFlatIndex: insertIndex + o.localOffset, endFlatIndex: null});
    for (const c of closes) groups.get(c.key)!.endFlatIndex = insertIndex + c.localOffset;
    stepOpenGroupKey.set(step, currentOpenKey);
    visibleDirty = true;
  }

  function computeVisible(): LogItem[] {
    const result: LogItem[] = [];
    let hidingStep = -1;
    let hideUntil = -1; // flat index (exclusive); Infinity means "hide through end of step"
    for (let i = 0; i < flat.length; i++) {
      const item = flat[i];
      if (item.kind === 'step-header') {
        result.push(item);
        hidingStep = -1;
        hideUntil = -1;
        continue;
      }
      if (!(stepExpandedState.get(item.step) ?? false)) continue; // whole step collapsed
      if (hidingStep === item.step && i < hideUntil) continue; // inside a collapsed group's interior
      if (item.cmd?.name === 'group' && !(groupExpandedState.get(item.key) ?? true)) {
        const span = groups.get(item.key)!;
        hidingStep = item.step;
        hideUntil = span.endFlatIndex ?? Infinity;
      }
      result.push(item);
    }
    return result;
  }

  return {
    getFlat: () => flat,
    getVisible: () => {
      if (visibleDirty) {
        visible = computeVisible();
        visibleDirty = false;
      }
      return visible;
    },
    getFlatIndexForKey: (key) => flatIndexByKey.get(key) ?? -1,
    getGroupSpan: (key) => groups.get(key),
    ensureStepHeader,
    appendStepLines,
    isStepExpanded: (step) => stepExpandedState.get(step) ?? false,
    setStepExpanded: (step, expanded) => {
      stepExpandedState.set(step, expanded);
      visibleDirty = true;
    },
    isGroupExpanded: (groupKey) => groupExpandedState.get(groupKey) ?? true,
    setGroupExpanded: (groupKey, expanded) => {
      groupExpandedState.set(groupKey, expanded);
      visibleDirty = true;
    },
  };
}
